import { NgClass, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup, EventParticipant } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { some } from 'lodash-es';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { EpcotConfigService } from '../common/epcot-config.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { SHEET_VIEW_OPEN } from './sheet.token';

@Component({
    selector: 'ms-yes-no-option-group',
    templateUrl: './yes-no-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        SlicePipe,
        OptionComponent,
        MarketHelpComponent,
        OptionPanelBottomActionComponent,
        IconCustomComponent,
        ParticipantImageComponent,
    ],
    providers: [ParticipantImageService],
})
export class YesNoOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    private readonly participantImageService = inject(ParticipantImageService);
    showMoreData: ShowMoreData;
    isEpcotEnabled = false;
    isShowHeadshotImage: boolean;
    participantType = ParticipantType;

    constructor(
        private optionGroupService: OptionGroupService,
        private marketGroupingConfig: MarketGroupingConfig,
        public sitecore: Sitecore,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    expanded = false;
    optionGroups: EventOption[][];
    visibleOptionsLimit: number;
    yesNoColumnClass: string;
    headerData: string[];

    ngOnChanges(): void {
        this.optionGroups = this.optionGroupService.getYesNoMarketGroups(this.optionGroup.options);
        this.headerData = [...new Set(this.optionGroup.options.map((item) => item.name || ''))];
        this.yesNoColumnClass = this.headerData.length === 2 ? 'triple' : 'double';
        const totalOptions = this.optionGroups.length;
        this.showMoreData = {
            totalCount: totalOptions,
            initialVisibleCount:
                this.isInSheetView || this.expanded
                    ? this.optionGroups.length
                    : (this.optionsLimitOverride ?? this.marketGroupingConfig.optionGroupRowsThreshold.yesNo),
            pageSize: totalOptions,
        };
        this.isShowHeadshotImage =
            this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.YesNo) &&
            this.isYesNoOptionPlayerMatched(this.event.players, this.optionGroup.options);
    }

    getOption(group: EventOption[], header: string): EventOption | undefined {
        return group.find((opt) => opt.name === header)!;
    }

    onShowMore({ state, visibleCount }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.visibleOptionsLimit = visibleCount;
    }

    private isYesNoOptionPlayerMatched(players: EventParticipant[], options: EventOption[]): boolean {
        return some(options, (option) => some(players, (player) => player.name === option.player1 || player.shortName === option.player1));
    }
}
